import React from "react";
import RichTextField from "../../../shared/RichTextField";
import MenuPlacement, { menuInitial, menuPayload } from "../../../shared/MenuPlacement";
import { Field, FieldArray, Form, Formik, FieldProps } from "formik";
import {
  Accordion,
  Button,
  Checkbox,
  Divider,
  Group,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import MediaPicker from "../../../Media/MediaPicker";

export interface IProcessStep { title: string; desc: string; }
export interface IBenefitItem { title: string; description: string; }
export interface ICooperationStep { number: string; title: string; desc: string; }
export interface IFaqItem { question: string; answer: string; }
export interface IExperienceFeature { title: string; desc: string; }
export interface IIconRowItem { iconUrl: string; label: string; description: string; }
export interface ISimpleFormMonday {
  boardId: string;
  nameColumnId: string;
  emailColumnId: string;
  phoneColumnId: string;
  descriptionColumnId: string;
  sourceColumnId: string;
}
export interface IHeroCardOpinion {
  rating: number;
  testimonial: string;
  authorName: string;
  date: string;
  googleReviewUrl: string;
}

/** Znaczniki do wyboru dla pojedynczego pola tekstowego. */
export const TAG_OPTIONS = [
  { value: "p", label: "Zwykły tekst (p)" },
  { value: "h1", label: "Nagłówek H1" },
  { value: "h2", label: "Nagłówek H2" },
  { value: "h3", label: "Nagłówek H3" },
  { value: "h4", label: "Nagłówek H4" },
  { value: "h5", label: "Nagłówek H5" },
  { value: "h6", label: "Nagłówek H6" },
];

/**
 * Mapa „nazwa pola -> znacznik" w obrębie jednej sekcji.
 * Pole nieustawione renderuje się znacznikiem domyślnym bloku, więc strony
 * zapisane wcześniej nie zmieniają ani wyglądu, ani struktury HTML.
 * W listach powtarzalnych (pytania FAQ, kroki, kafelki) klucz dotyczy rodzaju
 * pola, nie pojedynczego elementu — wszystkie pytania FAQ dzielą jeden poziom.
 */
export type TagMap = Record<string, string>;

export interface IPageFormValues {
  slug: string;
  title: string;
  active: boolean;
  /** Umiejscowienie w menu. `parentKey` to "offer:<id>"/"page:<id>";
   *  na kształt oczekiwany przez API zamienia to `menuPayload`. */
  menu: { show: boolean; parentKey: string; label: string; order: number };
  featured: { show: boolean; order: number };
  meta: { title: string; description: string; keyWords: string };
  sections: {
    hero: { enabled: boolean; tags: TagMap; title: string; subtitle: string; ctaText: string; bgImageUrl: string };
    heroCards: { enabled: boolean; tags: TagMap; label: string; opinions: IHeroCardOpinion[] };
    intro: {
      enabled: boolean; tags: TagMap;
      heading: string;
      paragraphsBefore: string;
      bullets: string[];
      paragraphsAfter: string;
      footerMain: string;
      footerNote: string;
      imageUrl: string;
      imageAlt: string;
    };
    experience: { enabled: boolean; tags: TagMap; heading: string; subheading: string; features: IExperienceFeature[] };
    textPhoto: {
      enabled: boolean; tags: TagMap;
      photoUrl: string;
      photoAlt: string;
      mainText: string;
      authorName: string;
      authorTitle1: string;
      authorTitle2: string;
    };
    iconRow: { enabled: boolean; tags: TagMap; items: IIconRowItem[] };
    process: { enabled: boolean; tags: TagMap; heading: string; steps: IProcessStep[] };
    benefits: { enabled: boolean; tags: TagMap; heading: string; description: string; items: IBenefitItem[] };
    cooperation: { enabled: boolean; tags: TagMap; heading: string; subheading: string; steps: ICooperationStep[] };
    faq: { enabled: boolean; tags: TagMap; items: IFaqItem[] };
    orangeCta: { enabled: boolean; tags: TagMap; heading: string; subheading: string; footerText: string };
    contact: { enabled: boolean; tags: TagMap; heading: string; subheading: string; dashedText: string };
    narrowContact: { enabled: boolean; tags: TagMap };
    simpleForm: {
      enabled: boolean; tags: TagMap;
      heading: string;
      subheading: string;
      dashedText: string;
      buttonText: string;
      successMessage: string;
      monday: ISimpleFormMonday;
    };
    simpleFormTop: {
      enabled: boolean; tags: TagMap;
      heading: string;
      subheading: string;
      dashedText: string;
      buttonText: string;
      successMessage: string;
      monday: ISimpleFormMonday;
    };
    footer: { heading: string; tags: TagMap };
  };
}

const defaultValues: IPageFormValues = {
  slug: "",
  title: "",
  active: true,
  menu: { show: false, parentKey: "", label: "", order: 0 },
  featured: { show: false, order: 0 },
  meta: { title: "", description: "", keyWords: "" },
  sections: {
    hero: { enabled: false, tags: {}, title: "", subtitle: "", ctaText: "", bgImageUrl: "" },
    heroCards: { enabled: false, tags: {}, label: "", opinions: [] },
    intro: {
      enabled: false, tags: {},
      heading: "",
      paragraphsBefore: "",
      bullets: [],
      paragraphsAfter: "",
      footerMain: "",
      footerNote: "",
      imageUrl: "",
      imageAlt: "",
    },
    experience: { enabled: false, tags: {}, heading: "", subheading: "", features: [] },
    textPhoto: {
      enabled: false, tags: {},
      photoUrl: "",
      photoAlt: "",
      mainText: "",
      authorName: "",
      authorTitle1: "",
      authorTitle2: "",
    },
    iconRow: { enabled: false, tags: {}, items: [] },
    process: { enabled: false, tags: {}, heading: "", steps: [] },
    benefits: { enabled: false, tags: {}, heading: "", description: "", items: [] },
    cooperation: { enabled: false, tags: {}, heading: "", subheading: "", steps: [] },
    faq: { enabled: false, tags: {}, items: [] },
    orangeCta: { enabled: false, tags: {}, heading: "", subheading: "", footerText: "" },
    contact: { enabled: false, tags: {}, heading: "", subheading: "", dashedText: "" },
    narrowContact: { enabled: false, tags: {} },
    simpleForm: {
      enabled: false, tags: {},
      heading: "",
      subheading: "",
      dashedText: "",
      buttonText: "",
      successMessage: "",
      monday: {
        boardId: "",
        nameColumnId: "",
        emailColumnId: "",
        phoneColumnId: "",
        descriptionColumnId: "",
        sourceColumnId: "",
      },
    },
    simpleFormTop: {
      enabled: false, tags: {},
      heading: "",
      subheading: "",
      dashedText: "",
      buttonText: "",
      successMessage: "",
      monday: {
        boardId: "",
        nameColumnId: "",
        emailColumnId: "",
        phoneColumnId: "",
        descriptionColumnId: "",
        sourceColumnId: "",
      },
    },
    footer: { heading: "", tags: {} },
  },
};

type Props = {
  onSubmit(values: IPageFormValues): void;
  page?: IPageFormValues & { _id?: string };
};

const SectionHeader: React.FC<{ label: string; name: string }> = ({ label, name }) => (
  <Group justify="space-between" align="center" w="100%">
    <Text fw={600}>{label}</Text>
    <Field name={`sections.${name}.enabled`}>
      {({ field, form }: FieldProps) => (
        <Switch
          checked={field.value}
          onChange={(e) => form.setFieldValue(field.name, e.currentTarget.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </Field>
  </Group>
);

/**
 * Lista wyboru znacznika HTML dla jednego pola tekstowego.
 * Puste = znacznik domyślny bloku, dlatego pole da się przywrócić do stanu
 * sprzed zmiany (przycisk czyszczenia), a strony nigdy nie tracą obecnego HTML-u.
 */
const TagPicker: React.FC<{
  section: string;
  field: string;
  defaultTag: string;
  label?: string;
}> = ({ section, field, defaultTag, label }) => (
  <Field name={`sections.${section}.tags.${field}`}>
    {({ field: f, form }: FieldProps) => (
      <Select
        label={label ?? "Znacznik"}
        data={TAG_OPTIONS}
        value={f.value || null}
        placeholder={`domyślny (${defaultTag})`}
        clearable
        allowDeselect
        comboboxProps={{ withinPortal: true }}
        onChange={(v) => form.setFieldValue(f.name, v ?? "")}
        w={190}
        styles={{ root: { flexShrink: 0 } }}
      />
    )}
  </Field>
);

/** Pole tekstowe z przypiętym po prawej wyborem znacznika. */
const TaggedField: React.FC<{
  section: string;
  field: string;
  defaultTag: string;
  children: React.ReactNode;
}> = ({ section, field, defaultTag, children }) => (
  <Group align="flex-end" gap="xs" wrap="nowrap" w="100%">
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    <TagPicker section={section} field={field} defaultTag={defaultTag} />
  </Group>
);

/**
 * Znaczniki dla listy powtarzalnej (pytania FAQ, kroki, kafelki).
 * Ustawienie obowiązuje wszystkie pozycje listy — różne poziomy nagłówków
 * w obrębie jednej listy są błędem SEO, więc celowo nie ma ustawień per pozycja.
 */
const ListTagPickers: React.FC<{
  section: string;
  fields: { field: string; label: string; defaultTag: string }[];
}> = ({ section, fields }) => (
  <Stack gap={4} mb="sm">
    <Text size="xs" c="dimmed">
      Znaczniki HTML — ustawienie dotyczy wszystkich pozycji poniżej
    </Text>
    <Group gap="xs" wrap="wrap">
      {fields.map((f) => (
        <TagPicker
          key={f.field}
          section={section}
          field={f.field}
          defaultTag={f.defaultTag}
          label={f.label}
        />
      ))}
    </Group>
  </Stack>
);

const PageForm: React.FC<Props> = ({ onSubmit, page }) => {
  const initial: IPageFormValues = page
    ? {
        slug: page.slug,
        title: page.title,
        active: page.active,
        ...menuInitial(page),
        meta: {
          title: page.meta?.title ?? "",
          description: page.meta?.description ?? "",
          keyWords: Array.isArray((page.meta as any)?.keyWords)
            ? (page.meta as any).keyWords.join(", ")
            : ((page.meta as any)?.keyWords ?? ""),
        },
        sections: {
          hero: {
            enabled: page.sections?.hero?.enabled ?? false,
            tags: page.sections?.hero?.tags ?? {},
            title: page.sections?.hero?.title ?? "",
            subtitle: page.sections?.hero?.subtitle ?? "",
            ctaText: page.sections?.hero?.ctaText ?? "",
            bgImageUrl: page.sections?.hero?.bgImageUrl ?? "",
          },
          heroCards: {
            enabled: page.sections?.heroCards?.enabled ?? false,
            tags: page.sections?.heroCards?.tags ?? {},
            label: page.sections?.heroCards?.label ?? "",
            opinions: (page.sections?.heroCards?.opinions ?? []).map((o) => ({
              rating: o.rating ?? 5,
              testimonial: o.testimonial ?? "",
              authorName: o.authorName ?? "",
              date: o.date ?? "",
              googleReviewUrl: o.googleReviewUrl ?? "",
            })),
          },
          intro: {
            enabled: page.sections?.intro?.enabled ?? false,
            tags: page.sections?.intro?.tags ?? {},
            heading: page.sections?.intro?.heading ?? "",
            paragraphsBefore: page.sections?.intro?.paragraphsBefore ?? "",
            bullets: page.sections?.intro?.bullets ?? [],
            paragraphsAfter: page.sections?.intro?.paragraphsAfter ?? "",
            footerMain: page.sections?.intro?.footerMain ?? "",
            footerNote: page.sections?.intro?.footerNote ?? "",
            imageUrl: page.sections?.intro?.imageUrl ?? "",
            imageAlt: page.sections?.intro?.imageAlt ?? "",
          },
          experience: {
            enabled: page.sections?.experience?.enabled ?? false,
            tags: page.sections?.experience?.tags ?? {},
            heading: page.sections?.experience?.heading ?? "",
            subheading: page.sections?.experience?.subheading ?? "",
            features: page.sections?.experience?.features ?? [],
          },
          textPhoto: {
            enabled: page.sections?.textPhoto?.enabled ?? false,
            tags: page.sections?.textPhoto?.tags ?? {},
            photoUrl: page.sections?.textPhoto?.photoUrl ?? "",
            photoAlt: page.sections?.textPhoto?.photoAlt ?? "",
            mainText: page.sections?.textPhoto?.mainText ?? "",
            authorName: page.sections?.textPhoto?.authorName ?? "",
            authorTitle1: page.sections?.textPhoto?.authorTitle1 ?? "",
            authorTitle2: page.sections?.textPhoto?.authorTitle2 ?? "",
          },
          iconRow: {
            enabled: page.sections?.iconRow?.enabled ?? false,
            tags: page.sections?.iconRow?.tags ?? {},
            items: page.sections?.iconRow?.items ?? [],
          },
          process: {
            enabled: page.sections?.process?.enabled ?? false,
            tags: page.sections?.process?.tags ?? {},
            heading: page.sections?.process?.heading ?? "",
            steps: page.sections?.process?.steps ?? [],
          },
          benefits: {
            enabled: page.sections?.benefits?.enabled ?? false,
            tags: page.sections?.benefits?.tags ?? {},
            heading: page.sections?.benefits?.heading ?? "",
            description: page.sections?.benefits?.description ?? "",
            items: page.sections?.benefits?.items ?? [],
          },
          cooperation: {
            enabled: page.sections?.cooperation?.enabled ?? false,
            tags: page.sections?.cooperation?.tags ?? {},
            heading: page.sections?.cooperation?.heading ?? "",
            subheading: page.sections?.cooperation?.subheading ?? "",
            steps: page.sections?.cooperation?.steps ?? [],
          },
          faq: {
            enabled: page.sections?.faq?.enabled ?? false,
            tags: page.sections?.faq?.tags ?? {},
            items: page.sections?.faq?.items ?? [],
          },
          orangeCta: {
            enabled: page.sections?.orangeCta?.enabled ?? false,
            tags: page.sections?.orangeCta?.tags ?? {},
            heading: page.sections?.orangeCta?.heading ?? "",
            subheading: page.sections?.orangeCta?.subheading ?? "",
            footerText: page.sections?.orangeCta?.footerText ?? "",
          },
          contact: {
            enabled: page.sections?.contact?.enabled ?? false,
            tags: page.sections?.contact?.tags ?? {},
            heading: page.sections?.contact?.heading ?? "",
            subheading: page.sections?.contact?.subheading ?? "",
            dashedText: page.sections?.contact?.dashedText ?? "",
          },
          narrowContact: {
            enabled: page.sections?.narrowContact?.enabled ?? false,
            tags: page.sections?.narrowContact?.tags ?? {},
          },
          simpleForm: {
            enabled: page.sections?.simpleForm?.enabled ?? false,
            tags: page.sections?.simpleForm?.tags ?? {},
            heading: page.sections?.simpleForm?.heading ?? "",
            subheading: page.sections?.simpleForm?.subheading ?? "",
            dashedText: page.sections?.simpleForm?.dashedText ?? "",
            buttonText: page.sections?.simpleForm?.buttonText ?? "",
            successMessage: page.sections?.simpleForm?.successMessage ?? "",
            monday: {
              boardId: page.sections?.simpleForm?.monday?.boardId ?? "",
              nameColumnId: page.sections?.simpleForm?.monday?.nameColumnId ?? "",
              emailColumnId: page.sections?.simpleForm?.monday?.emailColumnId ?? "",
              phoneColumnId: page.sections?.simpleForm?.monday?.phoneColumnId ?? "",
              descriptionColumnId: page.sections?.simpleForm?.monday?.descriptionColumnId ?? "",
              sourceColumnId: page.sections?.simpleForm?.monday?.sourceColumnId ?? "",
            },
          },
          simpleFormTop: {
            enabled: page.sections?.simpleFormTop?.enabled ?? false,
            tags: page.sections?.simpleFormTop?.tags ?? {},
            heading: page.sections?.simpleFormTop?.heading ?? "",
            subheading: page.sections?.simpleFormTop?.subheading ?? "",
            dashedText: page.sections?.simpleFormTop?.dashedText ?? "",
            buttonText: page.sections?.simpleFormTop?.buttonText ?? "",
            successMessage: page.sections?.simpleFormTop?.successMessage ?? "",
            monday: {
              boardId: page.sections?.simpleFormTop?.monday?.boardId ?? "",
              nameColumnId: page.sections?.simpleFormTop?.monday?.nameColumnId ?? "",
              emailColumnId: page.sections?.simpleFormTop?.monday?.emailColumnId ?? "",
              phoneColumnId: page.sections?.simpleFormTop?.monday?.phoneColumnId ?? "",
              descriptionColumnId: page.sections?.simpleFormTop?.monday?.descriptionColumnId ?? "",
              sourceColumnId: page.sections?.simpleFormTop?.monday?.sourceColumnId ?? "",
            },
          },
          footer: {
            heading: page.sections?.footer?.heading ?? "",
            tags: page.sections?.footer?.tags ?? {},
          },
        },
      }
    : defaultValues;

  return (
    <Formik
      initialValues={initial}
      enableReinitialize={Boolean(page)}
      onSubmit={(values) => {
        const payload = {
          ...values,
          ...menuPayload(values),
          meta: {
            ...values.meta,
            keyWords: values.meta.keyWords
              .split(",")
              .map((k) => k.trim())
              .filter(Boolean),
          },
        };
        onSubmit(payload as unknown as IPageFormValues);
      }}
    >
      {(formik) => (
        <Form>
          <Stack gap="md">
            {/* Basic info */}
            <Title order={3}>Podstawowe informacje</Title>

            <Field name="title">
              {({ field, meta }: FieldProps) => (
                <TextInput label="Nazwa strony" {...field} error={meta.touched ? meta.error : undefined} />
              )}
            </Field>

            <Field name="slug">
              {({ field, meta }: FieldProps) => (
                <TextInput
                  label="Slug (URL)"
                  description="np. inwestycje-gieldowe → rite.pl/inwestycje-gieldowe"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>

            <Field name="active">
              {({ field, form }: FieldProps) => (
                <Checkbox
                  label="Strona aktywna (widoczna publicznie)"
                  checked={field.value}
                  onChange={(e) => form.setFieldValue(field.name, e.currentTarget.checked)}
                />
              )}
            </Field>

            <Divider />
            <Title order={3}>Meta dane (SEO)</Title>

            <Field name="meta.title">
              {({ field, meta }: FieldProps) => (
                <TextInput label="Tytuł (meta)" {...field} error={meta.touched ? meta.error : undefined} />
              )}
            </Field>
            <Field name="meta.description">
              {({ field, meta }: FieldProps) => (
                <Textarea label="Opis (meta description)" {...field} error={meta.touched ? meta.error : undefined} />
              )}
            </Field>
            <Field name="meta.keyWords">
              {({ field, meta }: FieldProps) => (
                <TextInput
                  label="Słowa kluczowe (oddziel przecinkami)"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>

            <MenuPlacement kind="page" selfId={(page as any)?._id} />

            <Divider />
            <Title order={3}>Sekcje</Title>
            <Text size="sm" c="dimmed">Włącz sekcje, które mają być wyświetlane na stronie, i wypełnij ich treść.</Text>

            <Accordion multiple variant="separated">

              {/* HERO */}
              <Accordion.Item value="hero">
                <Accordion.Control>
                  <SectionHeader label="Hero" name="hero" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <TaggedField section="hero" field="title" defaultTag="h1">
                      <Field name="sections.hero.title">
                        {({ field }: FieldProps) => (
                          <Textarea label="Tytuł (Enter tworzy nową linię)" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="hero" field="subtitle" defaultTag="p">
                      <RichTextField
                        name="sections.hero.subtitle"
                        label="Podtytuł"
                      />
                    </TaggedField>
                    <Field name="sections.hero.ctaText">
                      {({ field }: FieldProps) => (
                        <TextInput label="Tekst przycisku CTA" {...field} />
                      )}
                    </Field>
                    <Field name="sections.hero.bgImageUrl">
                      {({ field, form }: FieldProps) => (
                        <MediaPicker
                          label="Zdjęcie w tle"
                          value={field.value ?? ""}
                          onChange={(url) => form.setFieldValue(field.name, url)}
                        />
                      )}
                    </Field>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* HERO CARDS (opinie pod heroem) */}
              <Accordion.Item value="heroCards">
                <Accordion.Control>
                  <SectionHeader label="Opinie pod Hero (karuzela)" name="heroCards" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <TaggedField section="heroCards" field="label" defaultTag="p">
                      <Field name="sections.heroCards.label">
                        {({ field }: FieldProps) => (
                          <TextInput label="Etykieta nad karuzelą" placeholder="Zaufali nam:" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <ListTagPickers
                      section="heroCards"
                      fields={[
                        { field: "opinionTestimonial", label: "Opinia", defaultTag: "p" },
                        { field: "opinionAuthorName", label: "Autor opinii", defaultTag: "p" },
                      ]}
                    />
                    <FieldArray name="sections.heroCards.opinions">
                      {({ push, remove }) => (
                        <Stack gap="xs">
                          <Text size="sm" fw={500}>Opinie</Text>
                          {formik.values.sections.heroCards.opinions.map((_, i) => (
                            <Stack key={i} gap="xs" p="sm" style={{ border: "1px solid #e9ecef", borderRadius: 8 }}>
                              <Group justify="space-between">
                                <Text size="sm" fw={600}>Opinia {i + 1}</Text>
                                <Button size="xs" color="red" variant="light" onClick={() => remove(i)}>Usuń</Button>
                              </Group>
                              <Field name={`sections.heroCards.opinions.${i}.authorName`}>
                                {({ field }: FieldProps) => <TextInput label="Autor" {...field} />}
                              </Field>
                              <Field name={`sections.heroCards.opinions.${i}.testimonial`}>
                                {({ field }: FieldProps) => (
                                  <Textarea label="Treść opinii" rows={4} autosize {...field} />
                                )}
                              </Field>
                              <Group grow align="flex-start">
                                <Field name={`sections.heroCards.opinions.${i}.rating`}>
                                  {({ field, form }: FieldProps) => (
                                    <TextInput
                                      label="Ocena (1-5)"
                                      type="number"
                                      min={1}
                                      max={5}
                                      value={field.value ?? 5}
                                      onChange={(e) => form.setFieldValue(field.name, Number(e.currentTarget.value))}
                                    />
                                  )}
                                </Field>
                                <Field name={`sections.heroCards.opinions.${i}.date`}>
                                  {({ field }: FieldProps) => (
                                    <TextInput label="Data" placeholder="2025-03-15 lub np. marzec 2025" {...field} />
                                  )}
                                </Field>
                              </Group>
                              <Field name={`sections.heroCards.opinions.${i}.googleReviewUrl`}>
                                {({ field }: FieldProps) => (
                                  <TextInput label="Link do opinii Google (opcjonalnie)" {...field} />
                                )}
                              </Field>
                            </Stack>
                          ))}
                          <Button
                            variant="light"
                            color="orange"
                            size="sm"
                            onClick={() => push({ rating: 5, testimonial: "", authorName: "", date: "", googleReviewUrl: "" })}
                          >
                            + Dodaj opinię
                          </Button>
                        </Stack>
                      )}
                    </FieldArray>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* SIMPLE FORM — GÓRNY (nowa, niezależna instancja) */}
              <Accordion.Item value="simpleFormTop">
                <Accordion.Control>
                  <SectionHeader label="Formularz standardowy — górny" name="simpleFormTop" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                      Formularz z polami: imię i nazwisko, adres e-mail, numer telefonu, opis sprawy.
                      Zgłoszenia trafiają na wskazaną tablicę Monday.com.
                    </Text>
                    <TaggedField section="simpleFormTop" field="heading" defaultTag="h2">
                      <Field name="sections.simpleFormTop.heading">
                        {({ field }: FieldProps) => (
                          <TextInput label="Nagłówek sekcji" placeholder="Skontaktuj się z nami" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="simpleFormTop" field="subheading" defaultTag="h2">
                      <Field name="sections.simpleFormTop.subheading">
                        {({ field }: FieldProps) => (
                          <Textarea
                            label="Tekst obok formularza (Enter tworzy nową linię)"
                            rows={4}
                            autosize
                            {...field}
                          />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="simpleFormTop" field="dashedText" defaultTag="p">
                      <Field name="sections.simpleFormTop.dashedText">
                        {({ field }: FieldProps) => (
                          <TextInput label="Dopisek (kreskowany)" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <Field name="sections.simpleFormTop.buttonText">
                      {({ field }: FieldProps) => (
                        <TextInput label="Tekst przycisku" placeholder="Wyślij zgłoszenie" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleFormTop.successMessage">
                      {({ field }: FieldProps) => (
                        <TextInput
                          label="Komunikat po wysłaniu"
                          placeholder="Dziękujemy za zgłoszenie! Skontaktujemy się wkrótce."
                          {...field}
                        />
                      )}
                    </Field>

                    <Divider label="Integracja Monday.com" labelPosition="center" />
                    <Text size="sm" c="dimmed">
                      ID kolumn znajdziesz w Monday.com (menu kolumny → „Customize column” → „Column ID”).
                      Imię i nazwisko trafia zawsze do nazwy elementu; dodatkowa kolumna tekstowa jest opcjonalna.
                    </Text>
                    <Field name="sections.simpleFormTop.monday.boardId">
                      {({ field }: FieldProps) => (
                        <TextInput label="Board ID (ID tablicy)" placeholder="np. 1234567890" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleFormTop.monday.emailColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput label="ID kolumny – adres e-mail" placeholder="np. email_mm35g8c9" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleFormTop.monday.phoneColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput label="ID kolumny – numer telefonu" placeholder="np. phone_mm35p7qa" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleFormTop.monday.descriptionColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput label="ID kolumny – opis sprawy" placeholder="np. long_text_mm35abcd" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleFormTop.monday.sourceColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput
                          label="ID kolumny – źródło (adres URL)"
                          description="Zapisuje adres strony, z której wysłano zgłoszenie (wraz z parametrami utm_*, gclid). Kolumna tekstowa."
                          placeholder="np. text_mm35ijkl"
                          {...field}
                        />
                      )}
                    </Field>
                    <Field name="sections.simpleFormTop.monday.nameColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput
                          label="ID kolumny – imię i nazwisko (opcjonalnie)"
                          placeholder="np. text_mm35efgh"
                          {...field}
                        />
                      )}
                    </Field>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* CONTACT */}
              <Accordion.Item value="contact">
                <Accordion.Control>
                  <SectionHeader label="Formularz kontaktowy" name="contact" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <TaggedField section="contact" field="heading" defaultTag="h2">
                      <Field name="sections.contact.heading">
                        {({ field }: FieldProps) => (
                          <TextInput label="Nagłówek sekcji" placeholder="Skontaktuj się z nami" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="contact" field="subheading" defaultTag="h2">
                      <Field name="sections.contact.subheading">
                        {({ field }: FieldProps) => (
                          <Textarea
                            label="Tekst obok formularza (Enter tworzy nową linię)"
                            placeholder="Wyślij nam metrykę lokalu, umowę i prospekt – sprawdzimy bezpłatnie..."
                            rows={4}
                            autosize
                            {...field}
                          />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="contact" field="dashedText" defaultTag="p">
                      <Field name="sections.contact.dashedText">
                        {({ field }: FieldProps) => (
                          <TextInput
                            label="Dopisek (kreskowany)"
                            placeholder="Wykonamy bezpłatną analizę umowy w 24h"
                            {...field}
                          />
                        )}
                      </Field>
                    </TaggedField>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* NARROW CONTACT STRIP (kalkulator odszkodowania) */}
              <Accordion.Item value="narrowContact">
                <Accordion.Control>
                  <SectionHeader label="Kalkulator odszkodowania" name="narrowContact" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm" c="dimmed">
                    Sekcja z kalkulatorem odszkodowania ma stałą treść — użyj przełącznika, aby ją włączyć lub wyłączyć.
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>

              {/* PROCESS */}
              <Accordion.Item value="process">
                <Accordion.Control>
                  <SectionHeader label="Nasz Proces (karuzela kroków)" name="process" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <TaggedField section="process" field="heading" defaultTag="h2">
                      <Field name="sections.process.heading">
                        {({ field }: FieldProps) => (
                          <TextInput label="Nagłówek sekcji" placeholder="NASZ PROCES" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <ListTagPickers
                      section="process"
                      fields={[
                        { field: "stepTitle", label: "Tytuł kroku", defaultTag: "h3" },
                        { field: "stepDesc", label: "Opis kroku", defaultTag: "p" },
                      ]}
                    />
                    <FieldArray name="sections.process.steps">
                      {({ push, remove }) => (
                        <Stack gap="xs">
                          {formik.values.sections.process.steps.map((_, i) => (
                            <Stack key={i} gap="xs" p="sm" style={{ border: "1px solid #e9ecef", borderRadius: 8 }}>
                              <Group justify="space-between">
                                <Text size="sm" fw={600}>Krok {i + 1}</Text>
                                <Button size="xs" color="red" variant="light" onClick={() => remove(i)}>Usuń</Button>
                              </Group>
                              <Field name={`sections.process.steps.${i}.title`}>
                                {({ field }: FieldProps) => <TextInput label="Tytuł" {...field} />}
                              </Field>
                              <RichTextField
                                name={`sections.process.steps.${i}.desc`}
                                label="Opis kroku"
                              />
                            </Stack>
                          ))}
                          <Button
                            variant="light"
                            color="orange"
                            size="sm"
                            onClick={() => push({ title: "", desc: "" })}
                          >
                            + Dodaj krok
                          </Button>
                        </Stack>
                      )}
                    </FieldArray>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* BENEFITS */}
              <Accordion.Item value="benefits">
                <Accordion.Control>
                  <SectionHeader label="Korzyści" name="benefits" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <TaggedField section="benefits" field="heading" defaultTag="h2">
                      <Field name="sections.benefits.heading">
                        {({ field }: FieldProps) => <TextInput label="Nagłówek" {...field} />}
                      </Field>
                    </TaggedField>
                    <TaggedField section="benefits" field="description" defaultTag="h2">
                      <Field name="sections.benefits.description">
                        {({ field }: FieldProps) => <Textarea label="Opis główny" {...field} />}
                      </Field>
                    </TaggedField>
                    <ListTagPickers
                      section="benefits"
                      fields={[
                        { field: "itemTitle", label: "Tytuł kafelka", defaultTag: "h3" },
                        { field: "itemDescription", label: "Opis kafelka", defaultTag: "p" },
                      ]}
                    />
                    <FieldArray name="sections.benefits.items">
                      {({ push, remove }) => (
                        <Stack gap="xs">
                          {formik.values.sections.benefits.items.map((_, i) => (
                            <Stack key={i} gap="xs" p="sm" style={{ border: "1px solid #e9ecef", borderRadius: 8 }}>
                              <Group justify="space-between">
                                <Text size="sm" fw={600}>Korzyść {i + 1}</Text>
                                <Button size="xs" color="red" variant="light" onClick={() => remove(i)}>Usuń</Button>
                              </Group>
                              <Field name={`sections.benefits.items.${i}.title`}>
                                {({ field }: FieldProps) => <TextInput label="Tytuł" {...field} />}
                              </Field>
                              <RichTextField
                                name={`sections.benefits.items.${i}.description`}
                                label="Opis"
                              />
                            </Stack>
                          ))}
                          <Button
                            variant="light"
                            color="orange"
                            size="sm"
                            onClick={() => push({ title: "", description: "" })}
                          >
                            + Dodaj korzyść
                          </Button>
                        </Stack>
                      )}
                    </FieldArray>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* ICON ROW */}
              <Accordion.Item value="iconRow">
                <Accordion.Control>
                  <SectionHeader label="Wiersz ikon (4 kafelki)" name="iconRow" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <ListTagPickers
                      section="iconRow"
                      fields={[
                        { field: "itemLabel", label: "Etykieta", defaultTag: "p" },
                        { field: "itemDescription", label: "Opis", defaultTag: "p" },
                      ]}
                    />
                    <FieldArray name="sections.iconRow.items">
                      {({ push, remove }) => (
                        <Stack gap="xs">
                          {formik.values.sections.iconRow.items.map((_, i) => (
                            <Stack key={i} gap="xs" p="sm" style={{ border: "1px solid #e9ecef", borderRadius: 8 }}>
                              <Group justify="space-between">
                                <Text size="sm" fw={600}>Kafelek {i + 1}</Text>
                                <Button size="xs" color="red" variant="light" onClick={() => remove(i)}>Usuń</Button>
                              </Group>
                              <Field name={`sections.iconRow.items.${i}.iconUrl`}>
                                {({ field, form }: FieldProps) => (
                                  <MediaPicker
                                    label="Ikona"
                                    value={field.value ?? ""}
                                    onChange={(url) => form.setFieldValue(field.name, url)}
                                  />
                                )}
                              </Field>
                              <Field name={`sections.iconRow.items.${i}.label`}>
                                {({ field }: FieldProps) => <TextInput label="Etykieta" {...field} />}
                              </Field>
                              <RichTextField
                                name={`sections.iconRow.items.${i}.description`}
                                label="Opis"
                              />
                            </Stack>
                          ))}
                          <Button
                            variant="light"
                            color="orange"
                            size="sm"
                            onClick={() => push({ iconUrl: "", label: "", description: "" })}
                          >
                            + Dodaj kafelek
                          </Button>
                        </Stack>
                      )}
                    </FieldArray>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* EXPERIENCE */}
              <Accordion.Item value="experience">
                <Accordion.Control>
                  <SectionHeader label="Doświadczenie (4 cechy)" name="experience" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <TaggedField section="experience" field="heading" defaultTag="h2">
                      <Field name="sections.experience.heading">
                        {({ field }: FieldProps) => (
                          <TextInput label="Nagłówek" placeholder="MAMY PONAD 10 LETNIE DOŚWIADCZENIE." {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="experience" field="subheading" defaultTag="h2">
                      <Field name="sections.experience.subheading">
                        {({ field }: FieldProps) => (
                          <Textarea label="Podtytuł" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <ListTagPickers
                      section="experience"
                      fields={[
                        { field: "featureTitle", label: "Tytuł cechy", defaultTag: "h3" },
                        { field: "featureDesc", label: "Opis cechy", defaultTag: "p" },
                      ]}
                    />
                    <FieldArray name="sections.experience.features">
                      {({ push, remove }) => (
                        <Stack gap="xs">
                          {formik.values.sections.experience.features.map((_, i) => (
                            <Stack key={i} gap="xs" p="sm" style={{ border: "1px solid #e9ecef", borderRadius: 8 }}>
                              <Group justify="space-between">
                                <Text size="sm" fw={600}>Cecha {i + 1}</Text>
                                <Button size="xs" color="red" variant="light" onClick={() => remove(i)}>Usuń</Button>
                              </Group>
                              <Field name={`sections.experience.features.${i}.title`}>
                                {({ field }: FieldProps) => (
                                  <Textarea label="Tytuł (Enter tworzy nową linię)" {...field} />
                                )}
                              </Field>
                              <RichTextField
                                name={`sections.experience.features.${i}.desc`}
                                label="Opis"
                              />
                            </Stack>
                          ))}
                          <Button
                            variant="light"
                            color="orange"
                            size="sm"
                            onClick={() => push({ title: "", desc: "" })}
                          >
                            + Dodaj cechę
                          </Button>
                        </Stack>
                      )}
                    </FieldArray>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* INTRO */}
              <Accordion.Item value="intro">
                <Accordion.Control>
                  <SectionHeader label="Sekcja wstępna (tekst + zdjęcie)" name="intro" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <TaggedField section="intro" field="heading" defaultTag="h2">
                      <Field name="sections.intro.heading">
                        {({ field }: FieldProps) => (
                          <Textarea label="Nagłówek" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="intro" field="paragraphsBefore" defaultTag="p">
                      <RichTextField
                        name="sections.intro.paragraphsBefore"
                        label="Akapity przed listą"
                        description="Każdy akapit osobno. Możesz wstawiać odnośniki."
                      />
                    </TaggedField>
                    <FieldArray name="sections.intro.bullets">
                      {({ push, remove }) => (
                        <Stack gap="xs">
                          <Text size="sm" fw={500}>Punkty listy</Text>
                          {formik.values.sections.intro.bullets.map((_, i) => (
                            <Group key={i} gap="xs" align="flex-start">
                              <Field name={`sections.intro.bullets.${i}`}>
                                {({ field }: FieldProps) => (
                                  <TextInput {...field} style={{ flex: 1 }} />
                                )}
                              </Field>
                              <Button size="xs" color="red" variant="light" onClick={() => remove(i)}>Usuń</Button>
                            </Group>
                          ))}
                          <Button
                            variant="light"
                            color="orange"
                            size="sm"
                            onClick={() => push("")}
                          >
                            + Dodaj punkt
                          </Button>
                        </Stack>
                      )}
                    </FieldArray>
                    <TaggedField section="intro" field="paragraphsAfter" defaultTag="p">
                      <RichTextField
                        name="sections.intro.paragraphsAfter"
                        label="Akapity po liście"
                        description="Każdy akapit osobno. Możesz wstawiać odnośniki."
                      />
                    </TaggedField>
                    <TaggedField section="intro" field="footerMain" defaultTag="p">
                      <Field name="sections.intro.footerMain">
                        {({ field }: FieldProps) => (
                          <TextInput label="Wyróżnienie pod tekstem" placeholder="Nawet do 50 000 PLN za mieszkanie*" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="intro" field="footerNote" defaultTag="p">
                      <Field name="sections.intro.footerNote">
                        {({ field }: FieldProps) => (
                          <TextInput label="Dopisek (kreskowany)" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <Field name="sections.intro.imageUrl">
                      {({ field, form }: FieldProps) => (
                        <MediaPicker
                          label="Zdjęcie"
                          value={field.value ?? ""}
                          onChange={(url) => form.setFieldValue(field.name, url)}
                        />
                      )}
                    </Field>
                    <Field name="sections.intro.imageAlt">
                      {({ field }: FieldProps) => (
                        <TextInput label="Opis zdjęcia (alt)" {...field} />
                      )}
                    </Field>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>


              {/* TEXT + PHOTO */}
              <Accordion.Item value="textPhoto">
                <Accordion.Control>
                  <SectionHeader label="Cytat z autorem (tekst + zdjęcie)" name="textPhoto" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <Field name="sections.textPhoto.photoUrl">
                      {({ field, form }: FieldProps) => (
                        <MediaPicker
                          label="Zdjęcie"
                          value={field.value ?? ""}
                          onChange={(url) => form.setFieldValue(field.name, url)}
                        />
                      )}
                    </Field>
                    <Field name="sections.textPhoto.photoAlt">
                      {({ field }: FieldProps) => (
                        <TextInput label="Opis zdjęcia (alt)" {...field} />
                      )}
                    </Field>
                    <TaggedField section="textPhoto" field="mainText" defaultTag="h2">
                      <RichTextField
                        name="sections.textPhoto.mainText"
                        label="Treść główna"
                      />
                    </TaggedField>
                    <TaggedField section="textPhoto" field="authorName" defaultTag="p">
                      <Field name="sections.textPhoto.authorName">
                        {({ field }: FieldProps) => (
                          <TextInput label="Imię i nazwisko autora" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="textPhoto" field="authorTitle1" defaultTag="p">
                      <Field name="sections.textPhoto.authorTitle1">
                        {({ field }: FieldProps) => (
                          <TextInput label="Tytuł autora — linia 1" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="textPhoto" field="authorTitle2" defaultTag="p">
                      <Field name="sections.textPhoto.authorTitle2">
                        {({ field }: FieldProps) => (
                          <TextInput label="Tytuł autora — linia 2" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>



              {/* COOPERATION */}
              <Accordion.Item value="cooperation">
                <Accordion.Control>
                  <SectionHeader label="Współpraca (kroki)" name="cooperation" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <TaggedField section="cooperation" field="heading" defaultTag="h2">
                      <Field name="sections.cooperation.heading">
                        {({ field }: FieldProps) => <TextInput label="Nagłówek (np. WSPÓŁPRACA)" {...field} />}
                      </Field>
                    </TaggedField>
                    <TaggedField section="cooperation" field="subheading" defaultTag="h2">
                      <Field name="sections.cooperation.subheading">
                        {({ field }: FieldProps) => (
                          <Textarea label="Podtytuł (Enter tworzy nową linię)" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <ListTagPickers
                      section="cooperation"
                      fields={[
                        { field: "stepTitle", label: "Tytuł kroku", defaultTag: "h3" },
                        { field: "stepDesc", label: "Opis kroku", defaultTag: "p" },
                      ]}
                    />
                    <FieldArray name="sections.cooperation.steps">
                      {({ push, remove }) => (
                        <Stack gap="xs">
                          {formik.values.sections.cooperation.steps.map((_, i) => (
                            <Stack key={i} gap="xs" p="sm" style={{ border: "1px solid #e9ecef", borderRadius: 8 }}>
                              <Group justify="space-between">
                                <Text size="sm" fw={600}>Krok {i + 1}</Text>
                                <Button size="xs" color="red" variant="light" onClick={() => remove(i)}>Usuń</Button>
                              </Group>
                              <Field name={`sections.cooperation.steps.${i}.number`}>
                                {({ field }: FieldProps) => <TextInput label="Numer (np. 01)" {...field} />}
                              </Field>
                              <Field name={`sections.cooperation.steps.${i}.title`}>
                                {({ field }: FieldProps) => <TextInput label="Tytuł" {...field} />}
                              </Field>
                              <RichTextField
                                name={`sections.cooperation.steps.${i}.desc`}
                                label="Opis kroku"
                              />
                            </Stack>
                          ))}
                          <Button
                            variant="light"
                            color="orange"
                            size="sm"
                            onClick={() => push({ number: String(formik.values.sections.cooperation.steps.length + 1).padStart(2, "0"), title: "", desc: "" })}
                          >
                            + Dodaj krok
                          </Button>
                        </Stack>
                      )}
                    </FieldArray>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* ORANGE CTA */}
              <Accordion.Item value="orangeCta">
                <Accordion.Control>
                  <SectionHeader label="Formularz CTA (pomarańczowe tło)" name="orangeCta" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <TaggedField section="orangeCta" field="heading" defaultTag="h2">
                      <Field name="sections.orangeCta.heading">
                        {({ field }: FieldProps) => <TextInput label="Nagłówek (np. ZGŁOŚ SIĘ JUŻ DZIŚ)" {...field} />}
                      </Field>
                    </TaggedField>
                    <TaggedField section="orangeCta" field="subheading" defaultTag="h2">
                      <Field name="sections.orangeCta.subheading">
                        {({ field }: FieldProps) => <Textarea label="Podtytuł" {...field} />}
                      </Field>
                    </TaggedField>
                    <TaggedField section="orangeCta" field="footerText" defaultTag="p">
                      <Field name="sections.orangeCta.footerText">
                        {({ field }: FieldProps) => <Textarea label="Tekst stopki" {...field} />}
                      </Field>
                    </TaggedField>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* SIMPLE FORM — DOLNY (był pod FAQ, teraz przed FAQ) */}
              <Accordion.Item value="simpleForm">
                <Accordion.Control>
                  <SectionHeader label="Formularz standardowy — dolny" name="simpleForm" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                      Formularz z polami: imię i nazwisko, adres e-mail, numer telefonu, opis sprawy.
                      Zgłoszenia trafiają na wskazaną tablicę Monday.com.
                    </Text>
                    <TaggedField section="simpleForm" field="heading" defaultTag="h2">
                      <Field name="sections.simpleForm.heading">
                        {({ field }: FieldProps) => (
                          <TextInput label="Nagłówek sekcji" placeholder="Skontaktuj się z nami" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="simpleForm" field="subheading" defaultTag="h2">
                      <Field name="sections.simpleForm.subheading">
                        {({ field }: FieldProps) => (
                          <Textarea
                            label="Tekst obok formularza (Enter tworzy nową linię)"
                            rows={4}
                            autosize
                            {...field}
                          />
                        )}
                      </Field>
                    </TaggedField>
                    <TaggedField section="simpleForm" field="dashedText" defaultTag="p">
                      <Field name="sections.simpleForm.dashedText">
                        {({ field }: FieldProps) => (
                          <TextInput label="Dopisek (kreskowany)" {...field} />
                        )}
                      </Field>
                    </TaggedField>
                    <Field name="sections.simpleForm.buttonText">
                      {({ field }: FieldProps) => (
                        <TextInput label="Tekst przycisku" placeholder="Wyślij zgłoszenie" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleForm.successMessage">
                      {({ field }: FieldProps) => (
                        <TextInput
                          label="Komunikat po wysłaniu"
                          placeholder="Dziękujemy za zgłoszenie! Skontaktujemy się wkrótce."
                          {...field}
                        />
                      )}
                    </Field>

                    <Divider label="Integracja Monday.com" labelPosition="center" />
                    <Text size="sm" c="dimmed">
                      ID kolumn znajdziesz w Monday.com (menu kolumny → „Customize column” → „Column ID”).
                      Imię i nazwisko trafia zawsze do nazwy elementu; dodatkowa kolumna tekstowa jest opcjonalna.
                    </Text>
                    <Field name="sections.simpleForm.monday.boardId">
                      {({ field }: FieldProps) => (
                        <TextInput label="Board ID (ID tablicy)" placeholder="np. 1234567890" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleForm.monday.emailColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput label="ID kolumny – adres e-mail" placeholder="np. email_mm35g8c9" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleForm.monday.phoneColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput label="ID kolumny – numer telefonu" placeholder="np. phone_mm35p7qa" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleForm.monday.descriptionColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput label="ID kolumny – opis sprawy" placeholder="np. long_text_mm35abcd" {...field} />
                      )}
                    </Field>
                    <Field name="sections.simpleForm.monday.sourceColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput
                          label="ID kolumny – źródło (adres URL)"
                          description="Zapisuje adres strony, z której wysłano zgłoszenie (wraz z parametrami utm_*, gclid). Kolumna tekstowa."
                          placeholder="np. text_mm35ijkl"
                          {...field}
                        />
                      )}
                    </Field>
                    <Field name="sections.simpleForm.monday.nameColumnId">
                      {({ field }: FieldProps) => (
                        <TextInput
                          label="ID kolumny – imię i nazwisko (opcjonalnie)"
                          placeholder="np. text_mm35efgh"
                          {...field}
                        />
                      )}
                    </Field>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* FAQ */}
              <Accordion.Item value="faq">
                <Accordion.Control>
                  <SectionHeader label="FAQ" name="faq" />
                </Accordion.Control>
                <Accordion.Panel>
                  <ListTagPickers
                    section="faq"
                    fields={[
                      { field: "sectionLabel", label: "Nadtytuł „FAQ”", defaultTag: "h2" },
                      { field: "sectionHeading", label: "Tytuł sekcji", defaultTag: "h2" },
                      { field: "question", label: "Pytanie", defaultTag: "p" },
                    ]}
                  />
                  <FieldArray name="sections.faq.items">
                    {({ push, remove }) => (
                      <Stack gap="xs">
                        {formik.values.sections.faq.items.map((_, i) => (
                          <Stack key={i} gap="xs" p="sm" style={{ border: "1px solid #e9ecef", borderRadius: 8 }}>
                            <Group justify="space-between">
                              <Text size="sm" fw={600}>Pytanie {i + 1}</Text>
                              <Button size="xs" color="red" variant="light" onClick={() => remove(i)}>Usuń</Button>
                            </Group>
                            <Field name={`sections.faq.items.${i}.question`}>
                              {({ field }: FieldProps) => <TextInput label="Pytanie" {...field} />}
                            </Field>
                            <RichTextField
                              name={`sections.faq.items.${i}.answer`}
                              label="Odpowiedź"
                              description="Możesz wstawiać odnośniki."
                            />
                          </Stack>
                        ))}
                        <Button
                          variant="light"
                          color="orange"
                          size="sm"
                          onClick={() => push({ question: "", answer: "" })}
                        >
                          + Dodaj pytanie
                        </Button>
                      </Stack>
                    )}
                  </FieldArray>
                </Accordion.Panel>
              </Accordion.Item>


              {/* FOOTER */}
              <Accordion.Item value="footer">
                <Accordion.Control>
                  <Text fw={600}>Stopka</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <TaggedField section="footer" field="heading" defaultTag="h2">
                    <Field name="sections.footer.heading">
                      {({ field }: FieldProps) => (
                        <Textarea
                          label="Nagłówek stopki (Enter tworzy nową linię)"
                          placeholder="Odzyskamy&#10;Twoje pieniądze&#10;od dewelopera!"
                          rows={3}
                          autosize
                          {...field}
                        />
                      )}
                    </Field>
                  </TaggedField>
                </Accordion.Panel>
              </Accordion.Item>

            </Accordion>

            <Button type="submit" size="md" radius="md" fullWidth mt="md">
              {page ? "Zapisz zmiany" : "Utwórz stronę"}
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default PageForm;
